import React, { useCallback, useState } from 'react';
import { Button, Field, Input, Spinner, Text } from '@fluentui/react-components';
import { ArrowRightRegular, InfoRegular, LinkRegular } from '@fluentui/react-icons';
import { FaFileExcel, FaMicrosoft } from 'react-icons/fa';
import { SiGoogledrive, SiGooglesheets } from 'react-icons/si';
import { connectionsService } from '../../api/connectionsService';
import type { Connection, Connector } from '../../api/types';

interface Props {
  connector?: Connector;
  connection?: Connection;
  onComplete?: (connectionId: string) => void;
  onSaved?: (connection: Connection) => void;
  onCancel?: () => void;
}

const providerGuidance = {
  google_sheets: {
    provider: 'Google',
    description: 'Connect a public Google Sheets workbook or supported spreadsheet file in Google Drive.',
    placeholder: 'https://docs.google.com/spreadsheets/d/… or https://drive.google.com/file/d/…',
    icons: (
      <>
        <SiGooglesheets aria-label="Google Sheets" color="#34a853" />
        <SiGoogledrive aria-label="Google Drive" color="#4285f4" />
      </>
    ),
    steps: [
      'Open the file in Google Sheets or Google Drive and select Share.',
      'Under General access, choose Anyone with the link.',
      'Set the role to Viewer, then select Copy link.',
    ],
    warning: 'Restricted links cannot be read. The file must remain set to “Anyone with the link” while this connection is in use.',
  },
  excel_online: {
    provider: 'Microsoft',
    description: 'Connect a public CSV or Excel workbook stored in OneDrive or SharePoint.',
    placeholder: 'https://1drv.ms/x/… or https://tenant.sharepoint.com/…',
    icons: (
      <>
        <FaFileExcel aria-label="Microsoft Excel" color="#217346" />
        <FaMicrosoft aria-label="Microsoft OneDrive or SharePoint" color="#00a4ef" />
      </>
    ),
    steps: [
      'Open the file in OneDrive or SharePoint and select Share.',
      'Open Link settings and choose Anyone with the link.',
      'Set the permission to Can view, apply the settings, then copy the link.',
    ],
    warning: 'Organization-only and specific-people links are not supported. The file must remain available to “Anyone with the link.”',
  },
} as const;

const PublicWorkbookSetup: React.FC<Props> = ({ connector: suppliedConnector, connection, onComplete, onSaved, onCancel }) => {
  const connector = suppliedConnector ?? connection?.connector;
  const config = connection?.metadataConfig || {};
  const [shareUrl, setShareUrl] = useState(typeof config.shareUrl === 'string' ? config.shareUrl : '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async () => {
    if (!connector) return;
    if (!shareUrl.trim()) {
      setError('Public share link is required');
      return;
    }
    setLoading(true);
    setError(null);
    let connectionId: string | undefined;
    let createdDraft = false;
    try {
      const saved = connection
        ? await connectionsService.updateConnection(connection.id, {
          metadataConfig: { shareUrl: shareUrl.trim() },
        })
        : await connectionsService.createConnection({
          connectorId: connector.id,
          name: connector.name,
          metadataConfig: { shareUrl: shareUrl.trim() },
        });
      connectionId = saved.id;
      createdDraft = !connection;
      if (connection) {
        onSaved?.(saved);
        return;
      }
      const result = await connectionsService.testConnection(saved.id);
      if (!result.success) {
        await connectionsService.deleteConnection(saved.id).catch(() => undefined);
        setError(result.message || 'The public file could not be read');
        return;
      }
      onComplete?.(saved.id);
    } catch (reason) {
      if (createdDraft && connectionId) {
        await connectionsService.deleteConnection(connectionId).catch(() => undefined);
      }
      setError(reason instanceof Error ? reason.message : 'Failed to connect file');
    } finally {
      setLoading(false);
    }
  }, [connection, connector, onComplete, onSaved, shareUrl]);

  if (!connector) return null;

  const guidance = providerGuidance[connector.id as keyof typeof providerGuidance]
    ?? providerGuidance.excel_online;

  return (
    <div className="public-workbook-setup">
      <header className="public-workbook-header">
        <div className="public-workbook-provider-icons" aria-label={`${guidance.provider} file providers`}>
          {guidance.icons}
        </div>
        <div className="public-workbook-heading">
          <Text size={500} weight="semibold">Connect {connector.name}</Text>
          <Text className="public-workbook-description">{guidance.description}</Text>
        </div>
      </header>

      <section className="public-workbook-instructions" aria-labelledby="public-workbook-instructions-title">
        <Text id="public-workbook-instructions-title" weight="semibold">Make the file viewable</Text>
        <ol>
          {guidance.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <Text size={200} className="public-workbook-warning">{guidance.warning}</Text>
      </section>

      <Field
        label="Public share link"
        hint="Only the share link is saved. Google and Microsoft credentials are never requested or stored."
        required
        validationMessage={error || undefined}
        validationState={error ? 'error' : 'none'}
      >
        <Input
          type="url"
          contentBefore={<LinkRegular />}
          value={shareUrl}
          placeholder={guidance.placeholder}
          onChange={(_, data) => setShareUrl(data.value)}
          disabled={loading}
        />
      </Field>

      <div className="public-workbook-datasets">
        <InfoRegular aria-hidden="true" />
        <Text size={200}>
          A CSV file creates one dataset. Each worksheet in an Excel workbook is available as a separate dataset.
        </Text>
      </div>

      <div className="public-workbook-actions">
        <Button
          appearance="primary"
          icon={loading ? <Spinner size="tiny" /> : <ArrowRightRegular />}
          iconPosition="after"
          disabled={loading || !shareUrl.trim()}
          onClick={connect}
        >
          {loading ? 'Validating file…' : connection ? 'Test & save changes' : 'Continue to dataset selection'}
        </Button>
        {onCancel && <Button disabled={loading} onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
};

export default PublicWorkbookSetup;